import { AnimatePresence, motion, useReducedMotion, type Transition, type Variants } from 'framer-motion'
import { CircleAlert, CircleCheck, Info, TriangleAlert, X } from 'lucide-react'
import { memo, useEffect } from 'react'
import { twMerge } from 'tailwind-merge'
import { Button } from '@highlight/ui'
import { type ChatToast, useChatToasts, useRemoveChatToast } from '../../../../store/toasts'

const noticeVariants: Variants = {
  hidden: { opacity: 0, y: 8 },
  visible: { opacity: 1, y: 0 },
}

const reducedMotionVariants: Variants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
}

const noticeTransition: Transition = { type: 'spring', bounce: 0, duration: 0.3 }

const MAX_VISIBLE_NOTICES = 3
const DEFAULT_NOTICE_MS = 6000

const ICON_BY_TYPE = {
  default: Info,
  success: CircleCheck,
  warning: TriangleAlert,
  error: CircleAlert,
} as const

const ICON_CLASS_BY_TYPE = {
  default: 'text-foreground-secondary',
  success: 'text-success-subtle',
  warning: 'text-warning-subtle',
  error: 'text-red',
} as const

/**
 * Chat notices (attachment rejections, failed edits, turn errors) rendered in
 * flow above the composer, in the input's `before` area, so they appear next
 * to the control the user just used on every surface the input is mounted.
 */
interface ChatNoticesProps {
  className?: string
}

export const ChatNotices = memo(({ className }: ChatNoticesProps) => {
  const notices = useChatToasts()

  return (
    <div aria-live="polite" className={twMerge('flex flex-col gap-2 [&:not(:empty)]:mb-3', className)}>
      <AnimatePresence initial={false}>
        {notices.slice(0, MAX_VISIBLE_NOTICES).map((notice) => (
          <Notice key={notice.id} notice={notice} />
        ))}
      </AnimatePresence>
    </div>
  )
})
ChatNotices.displayName = 'ChatNotices'

const Notice = memo(({ notice }: { notice: ChatToast }) => {
  const removeChatToast = useRemoveChatToast()
  const reducedMotion = useReducedMotion()
  const type = notice.type ?? 'default'
  const Icon = ICON_BY_TYPE[type]

  useEffect(() => {
    const timeout = setTimeout(() => removeChatToast(notice.id), notice.duration ?? DEFAULT_NOTICE_MS)
    return () => clearTimeout(timeout)
  }, [removeChatToast, notice.id, notice.duration])

  return (
    <motion.div
      layout={!reducedMotion}
      variants={reducedMotion ? reducedMotionVariants : noticeVariants}
      initial="hidden"
      animate="visible"
      exit="hidden"
      transition={noticeTransition}
      role={type === 'error' ? 'alert' : 'status'}
      className="bg-background corner-smooth flex w-full items-start gap-2.5 rounded-2xl border p-3"
    >
      <Icon aria-hidden="true" className={twMerge('mt-0.5 size-4 shrink-0', ICON_CLASS_BY_TYPE[type])} />
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        {notice.title && <span className="text-foreground break-words text-sm font-medium">{notice.title}</span>}
        {notice.message && (
          <span className="text-foreground-secondary break-words text-[13px] leading-[1.5]">{notice.message}</span>
        )}
      </div>
      <Button
        size="xs"
        shape="square"
        variant="ghost"
        aria-label="Dismiss"
        onClick={() => removeChatToast(notice.id)}
        className="text-foreground-placeholder hover:text-foreground -my-0.5 shrink-0"
      >
        <X aria-hidden="true" className="size-3.5" />
      </Button>
    </motion.div>
  )
})
Notice.displayName = 'ChatNotice'
